/**
 * useAgentApi Hook
 *
 * Main integration hook providing AgentApiClient to components.
 */

import { useState, useMemo } from 'react';
import { AgentApiClient } from '../services/agent/AgentApiClient';
import { useAuthentication } from './useAuthentication';
import { extractSessionIdFromUrl } from '../utils/urlParser';
import { logger } from '../utils/logger';

/**
 * useAgentApi Configuration
 */
export interface UseAgentApiConfig {
  /**
   * Optional agent session ID (if not provided, extracted from URL)
   */
  agentSessionId?: string;

  /**
   * API base URL override (defaults to VITE_API_GATEWAY_URL)
   */
  baseUrl?: string;

  /**
   * Skip authentication (for mock/storybook mode)
   * Default: false
   */
  skip?: boolean;

  /**
   * Pre-validated payment session from window.__PAYMENT_SESSION__ (read in main.tsx).
   * Forwarded to useAuthentication so credentials are never read from window directly.
   */
  initialSession?: PaymentSession;
}

/**
 * useAgentApi Hook Return Type
 */
export interface UseAgentApiResult {
  /**
   * Agent API client instance (available when authenticated)
   */
  apiClient: AgentApiClient | null;

  /**
   * Whether client is ready for use (authenticated)
   */
  isReady: boolean;

  /**
   * Loading state (authentication in progress)
   */
  isLoading: boolean;

  /**
   * Error that occurred during authentication
   */
  error: Error | null;

  /**
   * Agent session ID (from URL or config)
   */
  sessionId: string | null;

  /**
   * Whether session is expiring soon (< 5 minutes)
   */
  isExpiringSoon: boolean;

  /**
   * Time remaining until session expires (milliseconds)
   */
  timeRemaining: number | undefined;

  /**
   * Retry authentication
   */
  retry: () => void;

  /**
   * Re-authenticate with a rotated session token.
   * Use when the parent receives a new PaymentSession after token rotation.
   */
  refresh: (newSession: PaymentSession) => void;
}

/**
 * useAgentApi Hook
 *
 * Primary hook for Agent UI components to access backend APIs.
 * Handles authentication, token management, and provides AgentApiClient.
 *
 * @param config - Configuration options
 * @returns Agent API client and auth state
 *
 * @example
 * function MyComponent() {
 *   const { apiClient, isReady, error } = useAgentApi({
 *     baseUrl: '/bff/agent'
 *   });
 *
 *   if (error) return <ErrorView error={error} />;
 *   if (!isReady) return <LoadingView />;
 *
 *   return <AgentUI client={apiClient} />;
 * }
 */
export function useAgentApi(config: UseAgentApiConfig = {}): UseAgentApiResult {
  const {
    agentSessionId: providedSessionId,
    baseUrl = import.meta.env.VITE_API_GATEWAY_URL,
    skip = false,
    initialSession,
  } = config;

  // Extract sessionId from URL if not provided
  const sessionIdFromUrl = useMemo(() => {
    if (skip) return null; // Skip extraction in mock mode

    if (providedSessionId) {
      return providedSessionId;
    }

    const extractedId = extractSessionIdFromUrl();

    if (!extractedId) {
      logger.warn('No session ID found in URL');
      return null;
    }

    return extractedId;
  }, [providedSessionId, skip]);

  // Authenticate and get auth service (skip if in mock mode)
  const {
    authState,
    authService,
    apiClient: authApiClient,
    retry,
    refresh,
  } = useAuthentication(baseUrl, { skip }, initialSession);

  // Create AgentApiClient instance (memoized) — reuse the same ApiClient that receives credentials
  const [apiClient] = useState(
    () => new AgentApiClient(authApiClient, authService)
  );

  // Delegate to authService so threshold logic lives in one place only.
  const isExpiringSoon = authService.isExpiringSoon();

  // Return hook result
  return {
    apiClient: authState.isAuthenticated ? apiClient : null,
    isReady: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    sessionId: sessionIdFromUrl,
    isExpiringSoon,
    timeRemaining: authState.timeRemaining,
    retry,
    refresh,
  };
}
